"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ALL_COUNTRY_OPTIONS_WITH_FLAG } from "@bridge/constants";
import { faCheck, faChevronDown, faXmark } from "@icons/classic/regular";
import { Command, CommandEmpty, CommandInput, FlexContainer, FormControl, FormDescription, FormItem, FormLabel, FormMessage, input, Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../../components";
import { getFieldIds } from "./utils";
import type { FieldValues } from "react-hook-form";
import type { BaseComponentProps, BaseFieldProps } from "./types";
import type { FormOption } from "../../types";
const ITEM_HEIGHT = 36;
const LIST_HEIGHT = 300;

/**
 * CountrySelectField component for selecting a country from a searchable list.
 * Uses virtualization to efficiently render large lists of countries.
 * Defaults to ALL_COUNTRY_OPTIONS_WITH_FLAG from @bridge/constants.
 */
export type CountrySelectComponentProps = BaseComponentProps<typeof CommandInput> & {
  /** Country options array - defaults to ALL_COUNTRY_OPTIONS_WITH_FLAG */
  options?: FormOption[];
  /** Placeholder text shown when no country is selected */
  placeholder?: string;
  /** Message shown when no countries match the search */
  searchEmptyMessage?: string;
};
interface CountrySelectFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: CountrySelectComponentProps;
}
export function CountrySelectField<TFieldValues extends FieldValues>({
  field,
  fieldConfig,
  fieldState,
  componentProps,
  labelSize
}: CountrySelectFieldProps<TFieldValues>) {
  const {
    options = ALL_COUNTRY_OPTIONS_WITH_FLAG,
    placeholder = "Select a country",
    searchEmptyMessage = "No countries found",
    id,
    disabled: componentDisabled,
    className,
    ...restComponentProps
  } = componentProps;
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isDisabled = field.disabled || componentDisabled;
  const {
    fieldId,
    labelId,
    descriptionId,
    errorId,
    describedBy,
    isInvalid
  } = getFieldIds(fieldConfig, fieldState, id);

  // Get the selected country option
  const selectedOption = useMemo(() => {
    return options.find(option => option.value === field.value);
  }, [options, field.value]);

  // Filter options based on search input
  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) {
      return options;
    }
    const searchLower = searchValue.toLowerCase();
    return options.filter(option_0 => option_0.label.toLowerCase().includes(searchLower) || option_0.value.toLowerCase().includes(searchLower));
  }, [options, searchValue]);

  // Virtual list for performance
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => listElement,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5
  });

  // Reset search when popover closes
  useEffect(() => {
    if (!open) {
      setSearchValue("");
    }
  }, [open]);
  const handleSelect = useCallback((value: string) => {
    field.onChange(value === field.value ? "" : value);
    setOpen(false);
  }, [field]);
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    field.onChange("");
    setOpen(false);
  }, [field]);
  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      if (!hasOpenedOnce) {
        setHasOpenedOnce(true);
      }
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [hasOpenedOnce]);
  return <FormItem className="flex-1">
      <FormLabel htmlFor={fieldId} id={labelId} required={fieldConfig.required} size={labelSize} weight="medium">
        {fieldConfig.label}
      </FormLabel>
      <Popover modal={true} onOpenChange={handleOpenChange} open={open}>
        <PopoverTrigger asChild disabled={isDisabled}>
          <PopoverAnchor>
            <FormControl>
              <button className={input({
              className: `flex w-full cursor-pointer items-center justify-between text-left ${className || ""}`
            })} aria-describedby={describedBy} aria-expanded={open} aria-haspopup="listbox" aria-invalid={isInvalid} disabled={isDisabled} id={fieldId} ref={field.ref} role="combobox" type="button">
                <span className={selectedOption ? "text-content-primary" : "text-content-tertiary"}>
                  {selectedOption?.label || placeholder}
                </span>
                <FlexContainer className="gap-1" items="center">
                  {selectedOption && !isDisabled && <span className="cursor-pointer text-content-tertiary hover:text-content-secondary" onClick={handleClear}>
                      <FontAwesomeIcon className="size-4" icon={faXmark} />
                    </span>}
                  <FontAwesomeIcon className="size-4 text-content-tertiary" icon={faChevronDown} />
                </FlexContainer>
              </button>
            </FormControl>
          </PopoverAnchor>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start" onOpenAutoFocus={e_0 => {
        e_0.preventDefault();
      }} side="bottom" style={{
        width: "var(--radix-popover-trigger-width)"
      }}>
          {hasOpenedOnce && <Command shouldFilter={false}>
              <CommandInput {...restComponentProps} classNames={{
            wrapper: input({
              className: "border-0 border-b rounded-none"
            })
          }} onValueChange={setSearchValue} placeholder="Search countries..." ref={inputRef} value={searchValue} />
              {filteredOptions.length === 0 ? <CommandEmpty>{searchEmptyMessage}</CommandEmpty> : <div className="overflow-auto" ref={setListElement} style={{
            height: Math.min(LIST_HEIGHT, filteredOptions.length * ITEM_HEIGHT)
          }}>
                  <div style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative"
            }}>
                    {virtualizer.getVirtualItems().map(virtualItem => {
                const option_1 = filteredOptions[virtualItem.index];
                const isSelected = option_1.value === field.value;
                return <div className="absolute left-0 top-0 flex w-full cursor-pointer items-center justify-between px-2 py-2 text-sm hover:bg-background-secondary" data-selected={isSelected} key={option_1.value} onClick={() => handleSelect(option_1.value)} style={{
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`
                }}>
                          <span>{option_1.label}</span>
                          {isSelected && <FontAwesomeIcon className="h-4 w-4" icon={faCheck} />}
                        </div>;
              })}
                  </div>
                </div>}
            </Command>}
        </PopoverContent>
      </Popover>

      {fieldConfig.description && <FormDescription id={descriptionId}>
          {fieldConfig.description}
        </FormDescription>}
      {fieldState.error?.message && <FormMessage id={errorId}>{fieldState.error.message}</FormMessage>}
    </FormItem>;
}